import React, { useMemo, useSyncExternalStore } from 'react';
import { Group, Select } from '@mantine/core';
import {
  getVariableCatalogSnapshot,
  getVariableCatalogSource,
  loadVariableCatalog,
  subscribeVariableCatalog,
} from '../../utils/variableCatalogRegistry';
import { variableSelectOptions } from '../../utils/variableCatalog';
import { variableIsAnImage } from '../../../shared/variables/variableKinds';
import { withChosenOption } from './lostTarget';
import wiizSymbol from '../../../images/symbol.svg';

/**
 * Image variable, comme le sélecteur « Variables Images » du classique
 * (`ImageCascader`, `content.adminValue`) : même libellé, même logo devant
 * chaque variable, effaçable.
 *
 * Le choix copie la valeur, comme une variable de texte ou de bouton : le site
 * publié ne résout rien à l'affichage, « Rafraîchir les variables » la remet à
 * jour (`resolveVariableNodesDeep`). Dans un modèle, la valeur est vide et
 * s'écrit à la création du site.
 *
 * Le catalogue ne se charge qu'à l'ouverture de la liste (même règle que le
 * menu `@`) : la variable choisie s'affiche par son nom rangé dans la box.
 */
export function canPickVariableImage() {
  return !!getVariableCatalogSource();
}

const WiizSymbol = () => <img src={wiizSymbol} alt="" width={16} height={16} />;

const renderOption = ({ option }) => (
  <Group gap={8} wrap="nowrap">
    <WiizSymbol />
    {option.label}
  </Group>
);

export default function VariableImagePicker({ binding, onPick, onClear }) {
  const { catalog } = useSyncExternalStore(subscribeVariableCatalog, getVariableCatalogSnapshot);

  const options = useMemo(
    () => variableSelectOptions(catalog, (variable) => variableIsAnImage(variable.key)),
    [catalog],
  );

  const pick = (key) => {
    if (!key) {
      onClear();
      return;
    }
    const variable = catalog.byKey[key];
    if (!variable) return;
    onPick({
      variableKey: variable.key,
      variableName: variable.name,
      variableValue: variable.value || '',
    });
  };

  return (
    <Select
      mb={6}
      size="xs"
      aria-label="Variables Images"
      placeholder="Variables Images"
      data={withChosenOption(options, binding?.variableKey, binding?.variableName)}
      value={binding?.variableKey || null}
      onChange={pick}
      onDropdownOpen={loadVariableCatalog}
      renderOption={renderOption}
      leftSection={binding?.variableKey ? <WiizSymbol /> : null}
      searchable
      clearable
      clearButtonProps={{ 'aria-label': 'Effacer la variable' }}
      nothingFoundMessage="Aucune variable image"
      comboboxProps={{ withinPortal: false }}
    />
  );
}
